import { APP_URL } from "@shared/env";
import {
  mockTemplatesPerPage,
  defaultForm,
  difficultyMap,
  cognitiveMap,
  formatMap,
  difficultyLabels,
  cognitiveLabels,
  formatLabels,
  STUDENTS_PER_PAGE,
} from "./teacherMocksConstants";

export function difficultyBadgeVariant(difficultyLevel) {
  if (difficultyLevel === "medium") return "orange";
  if (difficultyLevel === "hard") return "red";
  return "lime";
}

export function cognitiveBadgeVariant(cognitiveLevel) {
  if (cognitiveLevel === "apply") return "purple";
  if (cognitiveLevel === "analyze") return "green";
  return "blue";
}

export function appendQueryValue(params, key, value) {
  if (Array.isArray(value)) {
    value.forEach((item) => params.append(`${key}[]`, item));
    return;
  }
  if (value !== "" && value !== null && value !== undefined) params.append(key, value);
}

export function managerQuery(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => appendQueryValue(search, key, value));
  return search.toString();
}

export function mockTemplatesQuery({ activeStatus, query, courseId, page, perPage = mockTemplatesPerPage }) {
  const normalizedQuery = query.trim();
  const q = { "q[s]": activeStatus === "draft" ? "created_at desc" : activeStatus === "enabled" ? "updated_at desc" : "name asc" };

  if (activeStatus === "draft") q["q[draft_eq]"] = true;
  if (activeStatus === "enabled") {
    q["q[draft_eq]"] = false;
    q["q[enabled_eq]"] = true;
  }
  if (activeStatus === "disabled") {
    q["q[draft_eq]"] = false;
    q["q[enabled_eq]"] = false;
  }

  if (courseId) q["q[course_id_eq]"] = courseId;
  if (normalizedQuery) {
    if (/^\d+$/.test(normalizedQuery)) {
      q["q[id_eq]"] = normalizedQuery;
    } else {
      q["q[name_or_course_name_cont]"] = normalizedQuery;
    }
  }

  return managerQuery({ ...q, page, per_page: perPage });
}

export function recentFilterDate(value) {
  if (!value) return null;

  const date = new Date();
  if (value === "1_week") date.setDate(date.getDate() - 7);
  if (value === "1_month") date.setMonth(date.getMonth() - 1);
  if (value === "3_months") date.setMonth(date.getMonth() - 3);
  if (value === "6_months") date.setMonth(date.getMonth() - 6);

  return ["1_week", "1_month", "3_months", "6_months"].includes(value)
    ? date.toISOString().split("T")[0]
    : null;
}

export function questionFiltersToQuery(filters, page = 1) {
  const q = { "q[s]": filters.s || "id desc" };
  const recentDate = recentFilterDate(filters.recentFilter);

  if (filters.idEq) q["q[id_eq]"] = filters.idEq;
  if (filters.contextOrTagsNameCont) q["q[context_or_tags_name_cont]"] = filters.contextOrTagsNameCont;
  if (filters.contextCont) q["q[context_cont]"] = filters.contextCont;
  if (filters.lessonsCourseIdIn?.length) q["q[lessons_course_id_in]"] = filters.lessonsCourseIdIn;
  if (filters.fromLevelsExpanded?.length) q["q[from_levels_expanded]"] = filters.fromLevelsExpanded;
  if (filters.statusIn?.length) q["q[status_in]"] = filters.statusIn;
  if (filters.difficultyLevelIn?.length) {
    q["q[difficulty_level_in]"] = filters.difficultyLevelIn.map((item) => difficultyMap[item] || item);
  }
  if (filters.cognitiveLevelIn?.length) {
    q["q[cognitive_level_in]"] = filters.cognitiveLevelIn.map((item) => cognitiveMap[item] || item);
  }
  if (filters.typeTagsIn?.length) q["type_tags_in"] = filters.typeTagsIn.join(",");
  if (filters.formatEq) q["q[format_eq]"] = formatMap[filters.formatEq] || filters.formatEq;
  if (filters.teacherIdEq) q["q[teacher_id_eq]"] = filters.teacherIdEq;
  if (filters.reviewerIdEq) q["q[reviewer_id_eq]"] = filters.reviewerIdEq;
  if (filters.tagsNameIn) q["q[tags_name_in]"] = filters.tagsNameIn.split(",").map((item) => item.trim()).filter(Boolean);
  if (filters.teacherNotesCont) q["q[teacher_notes_cont]"] = filters.teacherNotesCont;
  if (recentDate) q["q[created_at_gteq]"] = recentDate;
  [
    "aiGeneratedEq",
    "calcRequiredEq",
    "highlightedEq",
    "showInLessonQuizEq",
    "commentVideoUrlPresent",
    "commentTextPresent",
    "commentFilePresent",
    "commentVideoUrlOrCommentTextOrCommentFilePresent",
    "reviewedAtNull",
  ].forEach((key) => {
    if (filters[key]) q[`q[${key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)}]`] = filters[key];
  });

  return managerQuery({ ...q, page, per_page: 20 });
}

export function getFirstStem(question) {
  return question.firstStemContent || question.meta?.questionStems?.slice().sort((a, b) => a.position - b.position)?.[0]?.content;
}

export function getQuestionCourseNames(question) {
  const names = new Set();
  question.meta?.lessons?.forEach((lesson) => {
    const course = lesson?.meta?.level?.meta?.course;
    if (course?.name) names.add(course.name);
  });
  return Array.from(names);
}

export function stripHtml(value) {
  const element = document.createElement("div");
  element.innerHTML = value || "";
  return element.textContent || element.innerText || "";
}

export function previewText(value, maxLength = 180) {
  const text = stripHtml(value).replace(/\s+/g, " ").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
}

export function questionTypeTag(question) {
  if (question.highlighted) return { className: "type-recurring", label: "Recorrente" };
  if (question.calcRequired) return { className: "type-calc", label: "Com cálculo" };
  if (question.format === "case") return { className: "type-case", label: "Case" };
  if (question.showInLessonQuiz) return { className: "type-interactive", label: "Questão interativa" };
  return { className: "type-no-calc", label: "Sem cálculo" };
}

export function selectedTypeTagFilter(filters) {
  if (filters.calcRequiredEq === "false") return "no_calc";
  if (filters.showInLessonQuizEq === "true") return "interactive";
  if (filters.formatEq === "case") return "case";
  if (filters.calcRequiredEq === "true") return "calc";
  if (filters.highlightedEq === "true") return "recurring";
  return "";
}

export function selectedTypeTagFilters(filters) {
  return Array.isArray(filters.typeTagsIn) ? filters.typeTagsIn : [];
}

export function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("pt-BR").format(date);
}

export function formatAverageScore(value, totalQuestions) {
  const score = Number(value);
  const total = Number(totalQuestions) || 0;
  if (!Number.isFinite(score)) return { average: "0", averagePercent: "0%" };

  const rounded = Math.round(score * 10) / 10;
  const percentLabel = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  const correctAnswers = total > 0 ? Math.round((score / 100) * total) : 0;
  return { average: String(correctAnswers), averagePercent: `${percentLabel}%` };
}

export function formatDurationSeconds(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return "-";

  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours === 0) return `${remainingMinutes}min`;

  return `${hours}h${String(remainingMinutes).padStart(2, "0")}`;
}

export function mapTemplateToTeacherMock(template) {
  const questionsCount = template.meta?.stats?.questionsCount || template.meta?.questions?.length || 0;
  const finishedMocksCount = template.meta?.stats?.finishedMocksCount || 0;
  const assignedMocksCount = template.meta?.stats?.assignedMocksCount || 0;
  const score = formatAverageScore(template.meta?.stats?.averageScore, questionsCount);
  const id = String(template.id);

  return {
    id,
    name: template.name,
    course: template.meta?.course?.name || "-",
    courseId: template.courseId || template.meta?.course?.id || "",
    createdAt: formatDate(template.createdAt),
    questions: questionsCount,
    answered: finishedMocksCount,
    totalStudents: assignedMocksCount,
    average: score.average,
    averagePercent: score.averagePercent,
    duration: formatDurationSeconds(template.meta?.stats?.averageDurationSeconds),
    status: template.draft ? "draft" : template.enabled === false ? "disabled" : "enabled",
    publicUrl: `${APP_URL}/mockTemplates/${id}`,
    managerUrl: `/manager/v2/mock-templates/${id}/edit`,
    hasRanking: finishedMocksCount > 0,
    rankingMockId: template.meta?.stats?.lastFinishedMockId || null,
  };
}

export function mapTemplateToFormValues(template) {
  const questions = (template.meta?.questions || [])
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0));

  return {
    ...defaultForm,
    id: template.id,
    name: template.name || "",
    courseId: template.courseId ? String(template.courseId) : "",
    courseName: template.meta?.course?.name || "",
    enabled: template.enabled !== false,
    draft: !!template.draft,
    availabilityMode: template.publicationMode || defaultForm.availabilityMode,
    sendToAll: template.publicationMode === "all",
    triggerLevelId: template.triggerLevelId ? String(template.triggerLevelId) : "",
    notifyStudents: template.notifyStudents !== false,
    linkStudents: template.meta?.linkStudents || [],
    questions: questions.map((question, index) => ({
      questionId: Number(question.id),
      mockTemplatesQuestionId: question.mockTemplatesQuestionId
        ? Number(question.mockTemplatesQuestionId)
        : undefined,
      context: question.context,
      firstStemContent: question.firstStemContent,
      difficultyLevel: question.difficultyLevel,
      cognitiveLevel: question.cognitiveLevel,
      format: question.format,
      status: question.status,
      calcRequired: question.calcRequired,
      highlighted: question.highlighted,
      showInLessonQuiz: question.showInLessonQuiz,
      stemsCount: question.stemsCount || 0,
      position: question.position || index + 1,
    })),
  };
}

export function mapQuestionsToUpdateAttributes(questions, originalQuestions) {
  const current = questions.map((question, index) => {
    const attributes = {
      questionId: question.questionId,
      position: index + 1,
    };

    if (question.mockTemplatesQuestionId) {
      return { ...attributes, id: question.mockTemplatesQuestionId };
    }

    return attributes;
  });

  const removed = originalQuestions
    .filter((original) =>
      original.mockTemplatesQuestionId &&
      !questions.some((question) => question.mockTemplatesQuestionId === original.mockTemplatesQuestionId)
    )
    .map((question) => ({ id: question.mockTemplatesQuestionId, _destroy: true }));

  return [...current, ...removed];
}

export function mergeTemplateQuestionIds(questions, template) {
  const templateQuestionsById = new Map(
    (template?.meta?.questions || []).map((question) => [Number(question.id), question])
  );

  let changed = false;
  const result = questions.map((question) => {
    const templateQuestion = templateQuestionsById.get(Number(question.questionId));
    if (!templateQuestion?.mockTemplatesQuestionId) return question;
    const newId = Number(templateQuestion.mockTemplatesQuestionId);
    if (question.mockTemplatesQuestionId === newId) return question;
    changed = true;
    return { ...question, mockTemplatesQuestionId: newId };
  });

  return changed ? result : questions;
}

export function haveQuestionChanges(questions, originalQuestions) {
  if (questions.length !== originalQuestions.length) return true;

  return questions.some((question, index) => {
    const original = originalQuestions[index];
    return !original ||
      Number(question.questionId) !== Number(original.questionId) ||
      Number(question.mockTemplatesQuestionId || 0) !== Number(original.mockTemplatesQuestionId || 0);
  });
}

export function statusLabel(status) {
  if (status === "draft") return "Rascunho";
  if (status === "disabled") return "Desabilitado";
  return "Habilitado";
}

export function copyText(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
  return Promise.resolve();
}

export function draftCode(mock) {
  return String(mock.id);
}

export function draftName(mock) {
  if (!mock.name || mock.name === "Rascunho de simulado") return "Rascunho";
  if (/^rascunho\b/i.test(mock.name)) return mock.name;
  return `Rascunho — ${mock.name}`;
}

export function formatCreateError(error) {
  const body = error?.response?.body;
  const errors = body?.errors;

  if (Array.isArray(errors)) return errors.join(", ");
  if (errors && typeof errors === "object") {
    const messages = Object.values(errors).flat().filter(Boolean);
    if (messages.length) return messages.join(", ");
  }

  return body?.error || body?.message || "Nao foi possivel criar o simulado. Tente novamente.";
}

export function getRankingScore(item) {
  const score = Number(item.score ?? item.average ?? item.percent ?? 0);
  return Number.isFinite(score) ? score : 0;
}

export function getRankingCorrectAnswers(item, totalQuestions) {
  if (item.correctAnswers != null) return Number(item.correctAnswers) || 0;
  if (item.correct_answers != null) return Number(item.correct_answers) || 0;
  return Math.round((getRankingScore(item) / 100) * totalQuestions);
}

export function getRankingStudentName(item) {
  return item.userName || item.user_name || item.studentName || item.name || "Aluno";
}

export function getRankingStudentEmail(item) {
  return item.email || item.userEmail || item.user_email || item.studentEmail || "";
}

export function getRankingTime(item) {
  return item.duration || item.time || item.elapsedTime || item.elapsed_time || "-";
}

export function mapQuestionToSelected(question, position) {
  return {
    questionId: Number(question.id),
    context: question.context,
    firstStemContent: getFirstStem(question),
    difficultyLevel: question.difficultyLevel,
    cognitiveLevel: question.cognitiveLevel,
    format: question.format,
    status: question.status,
    calcRequired: question.calcRequired,
    highlighted: question.highlighted,
    showInLessonQuiz: question.showInLessonQuiz,
    stemsCount: question.meta?.questionStems?.length || 0,
    position,
  };
}

export function studentSearchQuery({ search, page, courseId }) {
  const params = new URLSearchParams();
  if (search.trim()) params.set("q[name_or_email_cont]", search.trim());
  if (courseId) params.set("course_id", String(courseId));
  params.set("paginated", "true");
  params.set("page", String(page));
  params.set("per_page", String(STUDENTS_PER_PAGE));
  return params.toString();
}

export function buildMockTemplatePayload(values, { draft = false } = {}) {
  const courseId = Number(values.courseId);
  const availabilityMode = values.availabilityMode || (values.sendToAll ? "all" : "specific");

  return {
    mockTemplate: {
      name: values.name.trim() || "Rascunho de simulado",
      courseId,
      message: "",
      enabled: draft ? false : values.enabled,
      draft,
      publicationMode: availabilityMode,
      triggerLevelId: availabilityMode === "module_completion" && values.triggerLevelId
        ? Number(values.triggerLevelId)
        : null,
      notifyStudents: values.notifyStudents,
      classroomReportEnabled: false,
      classroomIds: [],
      mockTemplatesQuestionsAttributes: values.questions.map((question, index) => ({
        ...(question.mockTemplatesQuestionId ? { id: question.mockTemplatesQuestionId } : {}),
        questionId: question.questionId,
        position: index + 1,
      })),
      ...(availabilityMode === "module_completion" && values.triggerLevelId ? {
        levelsMockTemplatesAttributes: [{
          levelId: Number(values.triggerLevelId),
          quantity: values.questions.length,
        }],
      } : {}),
    },
    sendToAll: !draft && availabilityMode === "all",
    studentIds: !draft && ["all", "specific"].includes(availabilityMode) ? values.studentIds : [],
  };
}

export function validate(values) {
  const errors = {};

  if (!values.name.trim()) errors.name = "Informe o nome do simulado.";
  if (!values.courseId) errors.courseId = "Selecione o curso.";
  if (values.questions.length < 5) errors.questions = "Adicione pelo menos 5 questoes.";
  if (values.availabilityMode === "specific" && values.studentIds.length === 0) {
    errors.studentIds = "Selecione ao menos um aluno.";
  }
  if (values.availabilityMode === "module_completion" && !values.triggerLevelId) {
    errors.triggerLevelId = "Selecione o modulo que dispara o simulado.";
  }

  return errors;
}

export { difficultyLabels, cognitiveLabels, formatLabels };
